import { useTranslation } from "react-i18next";
import type { Geofence, ThemeClasses } from "../types";
import { formatDate, formatCoordinate } from "../utils/formatters";
import {
  getTypeColor,
  getTypeText,
  getTypeIcon,
  getStatusColorByName,
  getShapeText,
} from "../utils/helpers";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";

interface GeofenceGridCardProps {
  geofence: Geofence;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (geofence: Geofence) => void;
  onViewMap: (geofence: Geofence) => void;
  onDelete?: (geofence: Geofence) => void;
}

export function GeofenceGridCard({
  geofence,
  themeClasses,
  isDarkMode,
  onEdit,
  onViewMap,
  onDelete,
}: GeofenceGridCardProps) {
  const { t } = useTranslation("adminSections");
  const statuses = useStatusesStore((state) => state.statuses);
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();
  // Obtener el status basado en status_id
  const status = statuses.find((s) => s.id === geofence.status_id);
  const statusName = status?.name || "Desconocido";
  // Yard-owned geofences are system-managed: read-only here, edited from Patios.
  const isTruckYard =
    geofence.type === "truck_yard" || !!geofence.truck_yard_id;

  // Función local para calcular tiempo transcurrido
  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp * 1000;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0)
      return t("geofences.timeAgo.days", {
        count: days,
        defaultValue: `hace ${days} día${days > 1 ? "s" : ""}`,
      });
    if (hours > 0)
      return t("geofences.timeAgo.hours", {
        count: hours,
        defaultValue: `hace ${hours} hora${hours > 1 ? "s" : ""}`,
      });
    if (minutes > 0)
      return t("geofences.timeAgo.minutes", {
        count: minutes,
        defaultValue: `hace ${minutes} minuto${minutes > 1 ? "s" : ""}`,
      });
    return t("geofences.timeAgo.seconds", "hace unos segundos");
  };
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all`}
    >
      {/* Geofence Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`w-10 h-10 ${getTypeColor(
              geofence.type,
            )} rounded-xl flex items-center justify-center`}
          >
            <i className={`${getTypeIcon(geofence.type)} text-lg`}></i>
          </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text}`}>
              {geofence.name}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {geofence.id}
            </p>
          </div>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColorByName(
            statusName,
          )}`}
        >
          {statusName}
        </span>
      </div>

      {/* Type and Shape */}
      <div className="flex items-center space-x-2 mb-4">
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(
            geofence.type,
          )}`}
        >
          {getTypeText(geofence.type)}
        </span>
        <span
          className={`px-2 py-1 ${isDarkMode ? "bg-gray-700" : "bg-gray-100"} ${
            themeClasses.textSecondary
          } rounded-full text-xs font-medium`}
        >
          {getShapeText(geofence.shape)}
        </span>
      </div>

      {/* Description */}
      {/* <p className={`text-sm ${themeClasses.textSecondary} mb-2`}>
        {geofence.description}
      </p> */}

      {/* Center Address */}
      {/* {geofence.address && (
        <p
          className={`flex items-center gap-2 text-sm ${themeClasses.textSecondary} mb-4`}
        >
          <span className="font-medium">Centro del perímetro: </span>
          <span
            className="inline-block max-w-[300px] truncate"
            title={geofence.address}
          >
            {geofence.address}
          </span>
        </p>
      )} */}

      {/* Geofence Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          {geofence?.shape === "circular" && (
            <>
              <span className={`text-sm ${themeClasses.textSecondary}`}>
                {t("geofences.details.radius", "Radio:")}
              </span>
              <span className={`text-sm ${themeClasses.text} font-medium`}>
                {geofence.coordinates && !Array.isArray(geofence.coordinates)
                  ? `${geofence.coordinates.radius} m`
                  : "N/A"}
              </span>
            </>
          )}
          {geofence?.shape === "polygonal" && (
            <>
              <span className={`text-sm ${themeClasses.textSecondary}`}>
                {t("geofences.details.area", "Área:")}
              </span>
              <span className={`text-sm ${themeClasses.text} font-medium`}>
                {geofence.area
                  ? `${(geofence.area / 1000000).toFixed(2)} m²`
                  : "N/A"}
              </span>
            </>
          )}
          {geofence?.shape === "linear" && (
            <>
              <span className={`text-sm ${themeClasses.textSecondary}`}>
                {t("geofences.details.corridorWidth", "Corredor:")}
              </span>
              <span className={`text-sm ${themeClasses.text} font-medium`}>
                {t("geofences.details.corridorWidthValue", {
                  defaultValue: "±{{buffer}} m",
                  buffer: geofence.buffer ?? LINEAR_BUFFER_DEFAULT,
                })}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("geofences.details.coordinates", "Coordenadas:")}
          </span>
          {geofence.shape === "circular" ? (
            <div className="flex items-center space-x-2">
              {geofence.coordinates && !Array.isArray(geofence.coordinates) ? (
                <>
                  <span
                    className={`text-sm ${themeClasses.text} font-mono text-xs`}
                  >
                    {formatCoordinate(geofence.coordinates.latitude)}
                  </span>
                  <span
                    className={`text-sm ${themeClasses.text} font-mono text-xs`}
                  >
                    {formatCoordinate(geofence.coordinates.longitude)}
                  </span>
                </>
              ) : (
                <span
                  className={`text-sm ${themeClasses.text} font-mono text-xs`}
                >
                  N/A
                </span>
              )}
            </div>
          ) : (
            <span className={`text-sm ${themeClasses.text} font-mono text-xs`}>
              {t("geofences.details.variable", "Variable")}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("geofences.details.created", "Creada:")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {formatDate(geofence.createdAt)}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-4">
        <div
          className={`p-3 ${isDarkMode ? "bg-gray-800" : "bg-gray-50"} rounded-lg flex items-center justify-between`}
        >
          <div className="flex items-center space-x-2">
            <i
              className={`ri-device-line text-sm ${themeClasses.textSecondary}`}
            ></i>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("geofences.stats.devices", "Dispositivos")}
            </span>
          </div>
          <p className={`text-lg font-bold ${themeClasses.text}`}>
            {geofence?.devices?.length ?? 0}
          </p>
        </div>
      </div>

      {/* Truck-yard geofences are managed from the Patios section. */}
      {isTruckYard && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-300 text-xs">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          <span>
            {t(
              "geofences.managedFromYard",
              "Esta geocerca pertenece a un patio de maniobras; gestiónala desde la sección de Patios de Maniobras.",
            )}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex space-x-2 mb-3">
        {!isMonitor && !isReadOnly && (
          <CRUDTooltip className="flex-1">
            <button
              className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}
              onClick={() => onEdit(geofence)}
              disabled={disableCRUD}
            >
              {t("geofences.actions.edit", "Editar")}
            </button>
          </CRUDTooltip>
        )}
        <button
          className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors flex-1"
          onClick={() => onViewMap(geofence)}
        >
          {t("geofences.actions.viewMap", "Ver Mapa")}
        </button>
        {onDelete && !isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              className="py-2 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => onDelete(geofence)}
              disabled={disableCRUD}
              title={t("geofences.actions.delete", "Eliminar")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </CRUDTooltip>
        )}
      </div>

      {/* Last Activity */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("geofences.details.lastUpdate", "Última actualización:")}{" "}
          {geofence.updatedAt?._seconds
            ? getTimeAgo(geofence.updatedAt._seconds)
            : "N/A"}
        </p>
      </div>
    </div>
  );
}
